import { Alert, Button, Checkbox, Form, Input, Space, notification } from 'antd';
import { CopyOutlined, DownloadOutlined } from '@ant-design/icons';
import React, { useEffect, useState } from 'react';
import totpService from '../../../services/admin/totp.service';

// Handles the forced 2FA setup flow during login:
// QR scan → confirm code → backup codes.
// on_success() — called after the user clicks Done on the backup codes screen.
// on_cancel()  — called when the user clicks "Back to login" or when setup/init fails.
const TotpSetupFlow = ({ setup_token, on_success, on_cancel }) => {
    const [confirmForm] = Form.useForm();
    const [state, setState] = useState({
        step: 'loading', // 'loading' | 'scan' | 'backup_codes'
        qr_code: null,
        setup_key: null,
        confirm_loading: false,
        confirm_error: null,
        backup_codes: [],
        codes_saved: false,
    });

    const initSetup = async () => {
        const res = await totpService.setupInit(setup_token).catch(err => {
            const msg = err?.response?.data?.message || 'Failed to load setup. Please try again.';
            notification.error({ message: msg });
            on_cancel?.();
            return null;
        });
        if (!res) return;
        setState(s => ({ ...s, step: 'scan', qr_code: res.qr_code, setup_key: res.setup_key }));
    };

    const onConfirm = async () => {
        const { totp_code } = confirmForm.getFieldsValue();
        setState(s => ({ ...s, confirm_loading: true, confirm_error: null }));
        const res = await totpService.setupConfirm(totp_code, setup_token).catch(err => {
            setState(s => ({
                ...s,
                confirm_loading: false,
                confirm_error: err?.response?.data?.message || 'Invalid code',
            }));
            return null;
        });
        if (!res) return;
        confirmForm.resetFields();
        setState(s => ({
            ...s,
            confirm_loading: false,
            step: 'backup_codes',
            backup_codes: res.backup_codes,
            codes_saved: false,
        }));
    };

    const copyText = (text) => {
        navigator.clipboard.writeText(text);
        notification.success({ message: 'Copied to clipboard', duration: 2 });
    };

    const downloadCodes = (codes) => {
        const blob = new Blob([codes.join('\n')], { type: 'text/plain' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'backup-codes.txt';
        a.click();
        URL.revokeObjectURL(url);
    };

    useEffect(() => {
        initSetup();
    }, []);

    // ── Loading ──────────────────────────────────────────────────────────────
    if (state.step === 'loading') {
        return <div style={{ color: '#666', padding: '16px 0' }}>Loading...</div>;
    }

    // ── Backup codes ─────────────────────────────────────────────────────────
    if (state.step === 'backup_codes') {
        return (
            <div style={{ textAlign: 'start' }}>
                <Alert
                    type="warning"
                    showIcon
                    message="These backup codes will NOT be shown again. Save them somewhere safe before continuing."
                    style={{ marginBottom: 20 }}
                />
                <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(2, 1fr)',
                    gap: 8,
                    background: '#f9f9f9',
                    border: '1px solid #e8e8e8',
                    borderRadius: 8,
                    padding: 16,
                    marginBottom: 16,
                    fontFamily: 'monospace',
                    fontSize: 14,
                    letterSpacing: 1,
                }}>
                    {state.backup_codes.map((code, i) => (
                        <div key={i}>{code}</div>
                    ))}
                </div>
                <Space style={{ marginBottom: 20 }}>
                    <Button icon={<CopyOutlined />} onClick={() => copyText(state.backup_codes.join('\n'))}>
                        Copy All
                    </Button>
                    <Button icon={<DownloadOutlined />} onClick={() => downloadCodes(state.backup_codes)}>
                        Download
                    </Button>
                </Space>
                <div style={{ marginBottom: 16 }}>
                    <Checkbox
                        checked={state.codes_saved}
                        onChange={e => setState(s => ({ ...s, codes_saved: e.target.checked }))}
                    >
                        I have saved my backup codes
                    </Checkbox>
                </div>
                <Button
                    type="primary"
                    size="large"
                    style={{ width: '100%' }}
                    disabled={!state.codes_saved}
                    onClick={() => on_success?.()}
                >
                    Done
                </Button>
            </div>
        );
    }

    // ── Scan QR ──────────────────────────────────────────────────────────────
    return (
        <div style={{ textAlign: 'start' }}>
            <p style={{ color: '#666', marginBottom: 20 }}>
                Scan the QR code with Google Authenticator, then enter the 6-digit code to confirm.
            </p>
            <div style={{ textAlign: 'center', margin: '0 0 24px' }}>
                <img src={state.qr_code} alt="2FA QR Code" style={{ width: 180, height: 180 }} />
            </div>
            <div style={{ marginBottom: 24 }}>
                <span style={{ fontSize: 12, color: '#666', display: 'block', marginBottom: 6 }}>
                    Can't scan? Enter this setup key manually:
                </span>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <code style={{
                        flex: 1,
                        background: '#f5f5f5',
                        padding: '6px 12px',
                        borderRadius: 4,
                        fontFamily: 'monospace',
                        fontSize: 14,
                        letterSpacing: 2,
                    }}>
                        {state.setup_key}
                    </code>
                    <Button size="small" icon={<CopyOutlined />} onClick={() => copyText(state.setup_key)}>
                        Copy
                    </Button>
                </div>
            </div>
            <Form form={confirmForm} onFinish={onConfirm}>
                <Form.Item name="totp_code" style={{ marginBottom: 8 }}>
                    <Input
                        autoFocus
                        size="large"
                        placeholder="6-digit code"
                        maxLength={6}
                        inputMode="numeric"
                        autoComplete="one-time-code"
                        style={{ width: 200 }}
                        onChange={e => {
                            const val = e.target.value.replace(/\D/g, '');
                            confirmForm.setFieldValue('totp_code', val);
                            if (state.confirm_error) setState(s => ({ ...s, confirm_error: null }));
                        }}
                    />
                </Form.Item>
                {state.confirm_error && (
                    <Alert
                        type="error"
                        message={state.confirm_error}
                        showIcon
                        style={{ marginBottom: 12, maxWidth: 400 }}
                    />
                )}
                <Space style={{ marginTop: 4 }}>
                    <Button onClick={() => on_cancel?.()}>
                        Back to login
                    </Button>
                    <Button type="primary" htmlType="submit" loading={state.confirm_loading}>
                        Confirm
                    </Button>
                </Space>
            </Form>
        </div>
    );
};

export default TotpSetupFlow;
